/**
 * Shared formatter functions
 * Memoized and reusable across components
 * All formatters respect the current locale from i18n
 */

import { getDefaultCurrencyForLocale } from "@home-finance-manager/shared"
import { format, parseISO } from "date-fns"
import { enUS, es, fr, type Locale } from "date-fns/locale"
import i18n from "./i18n"

/**
 * Get currency from browser locale (for non-authenticated users)
 */
function getCurrencyFromBrowserLocale(): string {
  const browserLocale = navigator.language || "en-US"
  return getDefaultCurrencyForLocale(browserLocale)
}

// Map i18n language codes to date-fns locales
export const dateLocales: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  es: es,
}

// Map i18n language codes to Intl locale strings
export const intlLocales: Record<string, string> = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
}

/**
 * Get the current locale from i18n
 */
export const getCurrentLocale = (): string => {
  return i18n.language || "en"
}

/**
 * Get the date-fns locale for the current language
 */
export const getDateLocale = (): Locale => {
  const lang = getCurrentLocale().split("-")[0]
  return dateLocales[lang] || enUS
}

/**
 * Get the Intl locale string for the current language
 */
export const getIntlLocale = (): string => {
  const lang = getCurrentLocale().split("-")[0]
  return intlLocales[lang] || "en-US"
}

/**
 * Format a number as currency
 * Respects the current locale for number formatting
 * @param amount - The amount to format
 * @param currency - Optional currency code (defaults to locale-based currency)
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  currency?: string
): string => {
  const currencyCode = currency || getCurrencyFromBrowserLocale()

  if (amount === null || amount === undefined) {
    // Return formatted zero with the currency
    return new Intl.NumberFormat(getIntlLocale(), {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0)
  }

  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount
  if (isNaN(numAmount)) {
    return new Intl.NumberFormat(getIntlLocale(), {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0)
  }

  return new Intl.NumberFormat(getIntlLocale(), {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount)
}

/**
 * Format a date string to a readable format
 * Respects the current locale for date formatting
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A"
  try {
    const date = parseISO(dateString)
    return format(date, "PPP", { locale: getDateLocale() })
  } catch {
    return "Invalid date"
  }
}

/**
 * Format a number with locale-aware formatting
 */
export const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return "0"
  const numValue = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(numValue)) return "0"
  return new Intl.NumberFormat(getIntlLocale()).format(numValue)
}

/**
 * Format a number as currency with custom decimal places
 * Respects the current locale for number formatting
 * @param amount - The amount to format
 * @param minimumFractionDigits - Minimum decimal places (default: 2)
 * @param maximumFractionDigits - Maximum decimal places (default: 2)
 * @param currency - Optional currency code (defaults to locale-based currency)
 */
export const formatCurrencyWithDecimals = (
  amount: number | string | null | undefined,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2,
  currency?: string
): string => {
  const currencyCode = currency || getCurrencyFromBrowserLocale()

  if (amount === null || amount === undefined) {
    return new Intl.NumberFormat(getIntlLocale(), {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(0)
  }

  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount
  if (isNaN(numAmount)) {
    return new Intl.NumberFormat(getIntlLocale(), {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(0)
  }

  return new Intl.NumberFormat(getIntlLocale(), {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numAmount)
}
